"use client";

/**
 * A virtual try at an archived round - `EX-VRT01`, `docs/29` §6.22, the
 * canvas's "Virtual rounds" page (the door, the virtual board, the result).
 *
 * Every state here is the server's (`viewer.virtual`): whether a try may
 * start and why not, the try's clock and its result. The page only decides
 * which of the server's answers to draw, and the clock it counts down is the
 * try's own `endedAt`, so a reload shows the same seconds the submit gate uses.
 */
import { useEffect, useState, type ReactNode } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { BookOpen, CalendarClock, Clock, Info, Lock, Play, Repeat, Star, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatClock, formatNumber } from "@/lib/format-datetime";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { Button } from "@/components/ui/button";
import { BusyIcon } from "@/components/ui/busy-icon";
import { Dialog } from "@/components/ui/dialog";
import { useVirtualRoundAction, useVirtualStandings } from "../../hooks";
import { useNow } from "../../lib/use-now";
import { formatClockTime, formatDuration } from "../../lib/format";
import { useContestDisplayZone } from "../../lib/display-zone";
import type { ContestVirtualAttempt, ContestViewerVirtual, PublicContestDetail } from "../../types";

/** The reader's virtual block, or `null` on a round that cannot be taken virtually. */
export function virtualOf(contest: PublicContestDetail): ContestViewerVirtual | null {
  return contest.viewer.virtual ?? null;
}

/**
 * Whether the reader's try at this round is running at `now`. The server's
 * `running` with the clock read again here, so the page turns over at the
 * second the try ends without waiting for the next read.
 */
export function virtualRunning(attempt: ContestVirtualAttempt | null | undefined, now: number | null): boolean {
  if (!attempt?.running) return false;
  return now === null || now < Date.parse(attempt.endedAt);
}

/**
 * The same answer for a page that must not re-render every second (the
 * editor): true until the try's end, then one re-render as it passes.
 */
export function useVirtualRunning(attempt: ContestVirtualAttempt | null | undefined): boolean {
  const endedAt = attempt?.running ? Date.parse(attempt.endedAt) : null;
  const [over, setOver] = useState(false);
  useEffect(() => {
    setOver(false);
    if (endedAt === null) return undefined;
    const wait = endedAt - Date.now();
    if (wait <= 0) {
      setOver(true);
      return undefined;
    }
    // Capped, because a timer past about 24.8 days fires at once.
    const timer = window.setTimeout(() => setOver(true), Math.min(wait, 2_000_000_000));
    return () => window.clearTimeout(timer);
  }, [endedAt]);
  return endedAt !== null && !over;
}

/** The try's length as the round prints it: `2h`, `১ ঘণ্টা ৩০ মিনিট`. */
export function virtualLength(minutes: number, locale: string): string {
  return formatDuration(new Date(0).toISOString(), new Date(minutes * 60_000).toISOString(), locale);
}

function secondsLeft(attempt: ContestVirtualAttempt, now: number | null): number {
  if (now === null) return Math.max(0, Math.round((Date.parse(attempt.endedAt) - Date.parse(attempt.startedAt)) / 1000));
  return Math.max(0, Math.round((Date.parse(attempt.endedAt) - now) / 1000));
}

/* ── The door, on the finished round's dark card ──────────────────────────── */

/**
 * "Take it virtually" and the sheet that says what that means, or the
 * server's reason it cannot, or the way back into a running try.
 */
export function VirtualDoor({
  contest,
  locale,
  onShowProblems,
}: {
  contest: PublicContestDetail;
  locale: string;
  onShowProblems: () => void;
}) {
  const t = useTranslations("contest.virtual");
  const zone = useContestDisplayZone();
  const now = useNow(1000);
  const signInHref = useSignInHref(locale);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { start } = useVirtualRoundAction(contest.slug);
  const virtual = virtualOf(contest);
  if (!virtual) return null;

  const length = virtualLength(virtual.lengthMinutes, locale);
  const attempt = virtual.attempt;
  const running = virtualRunning(attempt, now);
  const endAt = now === null ? null : new Date(now + virtual.lengthMinutes * 60_000).toISOString();

  if (attempt && running) {
    return (
      <div className="mt-5 rounded-xl border border-primary/40 bg-primary/10 p-4">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 h-[18px] w-[18px] shrink-0 text-accent-peach" aria-hidden />
          <div className="min-w-0">
            <p className="text-[15px] font-bold text-white">{t("resumeTitle")}</p>
            <p className="mt-1 text-[13.5px] leading-relaxed text-neutral-300">
              {t("resumeLine", { left: formatClock(secondsLeft(attempt, now), locale) })}
            </p>
          </div>
        </div>
        <Button type="button" className="mt-3 h-[46px] w-full rounded-[10px] text-[15px]" onClick={onShowProblems}>
          <Play className="h-4 w-4" aria-hidden />
          {t("resume")}
        </Button>
      </div>
    );
  }

  // A finished try is shown on the Overview, under Virtual, never on the door.
  if (attempt) return null;

  if (!virtual.available) {
    return virtual.reason ? (
      <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.04] p-3.5">
        <Info className="mt-0.5 h-[18px] w-[18px] shrink-0 text-neutral-400" aria-hidden />
        <p className="text-[13.5px] leading-relaxed text-neutral-300">{t(`refusal_${virtual.reason}`)}</p>
      </div>
    ) : null;
  }

  return (
    <div className="mt-5 rounded-xl border border-primary/40 bg-primary/10 p-4">
      <div className="flex items-start gap-3">
        <Clock className="mt-0.5 h-[18px] w-[18px] shrink-0 text-accent-peach" aria-hidden />
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-white">{t("doorTitle")}</p>
          <p className="mt-1 text-[13.5px] leading-relaxed text-neutral-300">{t("doorBody", { length })}</p>
          {virtual.taken > 0 ? (
            <p className="mt-1.5 text-xs text-neutral-400">{t("takenCount", { count: formatNumber(virtual.taken, locale) })}</p>
          ) : null}
        </div>
      </div>
      {contest.viewer.isAuthenticated ? (
        <Button type="button" className="mt-3 h-[46px] w-full rounded-[10px] text-[15px]" onClick={() => setSheetOpen(true)}>
          <Play className="h-4 w-4" aria-hidden />
          {t("take")}
        </Button>
      ) : (
        <Link
          href={signInHref}
          className="mt-3 flex h-[46px] w-full items-center justify-center gap-2 rounded-[10px] bg-primary text-[15px] font-semibold text-white hover:bg-primary/90"
        >
          {t("signInToTake")}
        </Link>
      )}

      <Dialog
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={t("sheetTitle", { title: contest.title_en })}
        closeLabel={t("close")}
        size="bottom"
        dismissible={!start.isPending}
        leading={
          <span className="inline-flex h-6 items-center rounded-full bg-primary/10 px-2.5 text-xs font-semibold text-primary">
            {t("sheetChip")}
          </span>
        }
      >
        <ul className="flex flex-col gap-3.5">
          {[
            { icon: Clock, title: t("rule1Title", { length }), body: t("rule1Body", { end: endAt ? formatClockTime(endAt, locale, zone) : "" }) },
            { icon: Lock, title: t("rule2Title"), body: t("rule2Body") },
            { icon: Repeat, title: t("rule3Title"), body: t("rule3Body") },
            { icon: BookOpen, title: t("rule4Title"), body: t("rule4Body") },
          ].map((rule) => (
            <li key={rule.title} className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-neutral-100">
                <rule.icon className="h-4 w-4 text-neutral-700" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-neutral-900">{rule.title}</p>
                <p className="mt-0.5 text-[13.5px] leading-relaxed text-neutral-600">{rule.body}</p>
              </div>
            </li>
          ))}
        </ul>
        {start.isError ? (
          <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {t("startFailed")}
          </p>
        ) : null}
        <div className="mt-5 flex flex-wrap justify-end gap-2.5 border-t border-neutral-100 pt-4">
          <Button type="button" variant="outline" onClick={() => setSheetOpen(false)} disabled={start.isPending}>
            {t("notNow")}
          </Button>
          <Button
            type="button"
            data-autofocus
            disabled={start.isPending}
            onClick={() =>
              start.mutate(undefined, {
                onSuccess: () => {
                  setSheetOpen(false);
                  onShowProblems();
                },
              })
            }
          >
            <BusyIcon busy={start.isPending} icon={<Play className="h-4 w-4" />} />
            {t("start", { length })}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

/* ── The bar over the page while the try runs ─────────────────────────────── */

/** Time left, the minute of the round, and Finish now behind a question. */
export function VirtualBar({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.virtual");
  const now = useNow(1000);
  const [asking, setAsking] = useState(false);
  const { finish } = useVirtualRoundAction(contest.slug);
  const attempt = virtualOf(contest)?.attempt ?? null;
  if (!attempt || !virtualRunning(attempt, now)) return null;

  const total = attempt.lengthMinutes * 60;
  const left = secondsLeft(attempt, now);
  const minute = Math.min(attempt.lengthMinutes, Math.floor((total - left) / 60));
  const progress = Math.min(100, Math.max(0, ((total - left) / total) * 100));

  return (
    <section
      aria-label={t("barChip")}
      className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-[14px] border border-neutral-800 bg-neutral-950 px-4 py-3.5 text-white sm:px-5"
    >
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-primary/20 px-2.5 text-xs font-semibold text-accent-peach">
        <span className="h-[7px] w-[7px] rounded-full bg-primary" aria-hidden />
        {t("barChip")}
      </span>
      <div className="flex flex-col">
        <span className="text-[11px] uppercase tracking-[0.06em] text-neutral-400">{t("timeLeft")}</span>
        <span className="font-mono text-2xl font-bold tabular-nums" role="timer" aria-live="off">
          {formatClock(left, locale)}
        </span>
      </div>
      <div className="flex min-w-[180px] flex-1 flex-col gap-2">
        <span className="text-[13px] tabular-nums text-neutral-300">
          {t("minuteOf", {
            minute: formatNumber(minute, locale),
            length: formatNumber(attempt.lengthMinutes, locale),
          })}
        </span>
        <div className="h-1.5 overflow-hidden rounded-full bg-white/10" aria-hidden>
          <div className="h-full rounded-full bg-primary transition-[width] duration-1000" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <Button type="button" variant="outlineDark" onClick={() => setAsking(true)}>
        {t("finish")}
      </Button>

      <Dialog
        open={asking}
        onClose={() => setAsking(false)}
        title={t("finishTitle")}
        description={t("finishBody")}
        closeLabel={t("close")}
        dismissible={!finish.isPending}
      >
        <div className="mt-2 flex flex-wrap justify-end gap-2.5">
          <Button type="button" variant="outline" data-autofocus onClick={() => setAsking(false)} disabled={finish.isPending}>
            {t("keepGoing")}
          </Button>
          <Button
            type="button"
            variant="ink"
            disabled={finish.isPending}
            onClick={() => finish.mutate(undefined, { onSuccess: () => setAsking(false) })}
          >
            <BusyIcon busy={finish.isPending} icon={<Trophy className="h-4 w-4" />} />
            {t("finishYes")}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}

/* ── The result, on the Overview, apart from every real result ────────────── */

/**
 * The finished try's card. The rank comes from the board's own read, which
 * writes the result the first time it is asked after the end, so the card is
 * never out of step with "See where you stood".
 */
export function VirtualResultCard({
  contest,
  locale,
  onShowBoard,
  onShowProblems,
}: {
  contest: PublicContestDetail;
  locale: string;
  onShowBoard: () => void;
  onShowProblems: () => void;
}) {
  const t = useTranslations("contest.virtual");
  const now = useNow(30_000);
  const attempt = virtualOf(contest)?.attempt ?? null;
  const ended = Boolean(attempt) && !virtualRunning(attempt, now);
  const board = useVirtualStandings(contest.slug, 1, ended, false);
  if (!attempt || !ended) return null;

  const you = board.data?.you ?? null;
  const rows = board.data?.rows ?? [];
  const own = rows.find((row) => row.virtual) ?? null;
  const rank = attempt.result?.rank ?? you?.rank ?? null;
  const total = attempt.result?.fieldSize ?? board.data?.total ?? null;
  const solved = attempt.result?.solved ?? own?.solved ?? null;
  const icpc = contest.scoringFormat === "icpc";
  const problems = contest.problems?.length ?? 0;
  const usedSeconds = Math.round((Date.parse(attempt.endedAt) - Date.parse(attempt.startedAt)) / 1000);

  return (
    <section aria-labelledby="virtual-result" className="rounded-2xl bg-neutral-950 p-5 text-white sm:p-6">
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex h-6 items-center rounded-full bg-primary/20 px-2.5 text-xs font-semibold text-accent-peach">
          {t("resultChip")}
        </span>
        <span className="inline-flex h-6 items-center rounded-full bg-white/10 px-2.5 text-xs text-neutral-300">{contest.title_en}</span>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-x-8 gap-y-3">
        <div>
          <h2 id="virtual-result" className="text-[15px] font-medium text-neutral-400">
            {t("resultTitle")}
          </h2>
          <p className="mt-1 text-4xl font-bold tabular-nums tracking-tight sm:text-[46px]">
            {rank !== null && total !== null ? (
              t("rankOf", { rank: formatNumber(rank, locale), total: formatNumber(total, locale) })
            ) : (
              <span className="text-lg font-medium text-neutral-400">{t("resultWorking")}</span>
            )}
          </p>
        </div>
        <dl className="flex flex-wrap gap-x-7 gap-y-2 pb-1.5">
          <div>
            <dt className="text-xs text-neutral-400">{t("solvedLabel")}</dt>
            <dd className="text-[22px] font-bold tabular-nums">
              {solved !== null ? t("solvedOf", { solved: formatNumber(solved, locale), total: formatNumber(problems, locale) }) : "·"}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-neutral-400">{icpc ? t("penaltyLabel") : t("scoreLabel")}</dt>
            <dd className="text-[22px] font-bold tabular-nums">
              {formatNumber(icpc ? (attempt.result?.penaltyMinutes ?? own?.penaltyMinutes ?? 0) : (attempt.result?.score ?? own?.score ?? 0), locale)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-neutral-400">{t("timeLabel")}</dt>
            <dd className="text-[22px] font-bold tabular-nums">{formatClock(usedSeconds, locale)}</dd>
          </div>
        </dl>
      </div>
      {own ? (
        <ul className="mt-4 flex flex-wrap gap-2" aria-label={t("viewVirtual")}>
          {own.cells.map((cell) => (
            <li key={cell.letter} className="flex flex-col items-center gap-1.5">
              <span className="text-xs font-bold text-neutral-400">{cell.letter}</span>
              <span
                className={cn(
                  "inline-flex h-[30px] min-w-[58px] items-center justify-center rounded-lg px-2 font-mono text-[13px] font-semibold tabular-nums",
                  cell.solved
                    ? "bg-emerald-700 text-white"
                    : cell.attempts > 0
                      ? "bg-rose-500/20 text-rose-300"
                      : "bg-white/[0.06] text-neutral-500",
                )}
              >
                {cell.solved
                  ? `${formatNumber(cell.solvedAtMinute ?? 0, locale)}${cell.attempts > 1 ? ` +${formatNumber(cell.attempts - 1, locale)}` : ""}`
                  : cell.attempts > 0
                    ? `−${formatNumber(cell.attempts, locale)}`
                    : "·"}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="mt-4 rounded-[10px] bg-white/5 px-3.5 py-3 text-[13.5px] leading-relaxed text-neutral-300">{t("resultNote")}</p>
      <div className="mt-4 flex flex-wrap gap-2.5">
        <Button type="button" onClick={onShowProblems}>
          <BookOpen className="h-4 w-4" aria-hidden />
          {t("readEditorial")}
        </Button>
        <Button type="button" variant="outlineDark" onClick={onShowBoard}>
          <Trophy className="h-4 w-4" aria-hidden />
          {t("seeBoard")}
        </Button>
      </div>
    </section>
  );
}

/* ── The Standings tab for somebody with a try ────────────────────────────── */

/**
 * The ghost board while the try runs; after it, the reader chooses between
 * where they stood and the round's final board.
 */
export function VirtualBoardSwitch({
  contest,
  ghost,
  final,
}: {
  contest: PublicContestDetail;
  ghost: ReactNode;
  final: ReactNode;
}) {
  const t = useTranslations("contest.virtual");
  const now = useNow(30_000);
  const [view, setView] = useState<"virtual" | "final">("virtual");
  const attempt = virtualOf(contest)?.attempt ?? null;
  if (!attempt) return <>{final}</>;
  if (virtualRunning(attempt, now)) return <>{ghost}</>;
  return (
    <div className="space-y-4">
      <div role="group" aria-label={t("viewLabel")} className="inline-flex gap-1 rounded-[10px] bg-neutral-100 p-1">
        <Button type="button" variant="segment" size="sm" aria-pressed={view === "virtual"} onClick={() => setView("virtual")}>
          <Star className="h-4 w-4" aria-hidden />
          {t("viewVirtual")}
        </Button>
        <Button type="button" variant="segment" size="sm" aria-pressed={view === "final"} onClick={() => setView("final")}>
          <CalendarClock className="h-4 w-4" aria-hidden />
          {t("viewFinal")}
        </Button>
      </div>
      {view === "virtual" ? ghost : final}
    </div>
  );
}
